/**
 * Meeting Minutes — domain constants for the Meeting Minutes Register.
 *
 * Entries in a set of minutes are typed by category. The category drives the
 * chip tone, the icon, the helper copy under the picker, and whether the entry
 * carries an owner / due date / open-done status (actions only).
 *
 * Tone maps use Nimbus design-system tone names. Consumers render them as:
 *   <span className={`np-chip np-tone-${tone}`}>…</span>
 */

import type { MinuteCategory, MinutesStatus } from '../types/models';

// ─── Entry category ───────────────────────────────────────────────────────────

/** Display order for the category picker and for grouped views. */
export const MINUTE_CATEGORIES: MinuteCategory[] = [
  'action',
  'decision',
  'risk',
  'blocker',
  'dependency',
  'information',
];

export const MINUTE_CATEGORY_LABELS: Record<MinuteCategory, string> = {
  action:      'Action',
  decision:    'Decision',
  risk:        'Risk',
  blocker:     'Blocker',
  dependency:  'Dependency',
  information: 'Information',
};

/** Nimbus tone per category. */
export const MINUTE_CATEGORY_TONE: Record<MinuteCategory, string> = {
  action:      'primary',
  decision:    'success',
  risk:        'warning',
  blocker:     'danger',
  dependency:  'info',
  information: 'neutral',
};

/** RemixIcon name per category. */
export const MINUTE_CATEGORY_ICON: Record<MinuteCategory, string> = {
  action:      'ri-play-circle-line',
  decision:    'ri-check-double-line',
  risk:        'ri-error-warning-line',
  blocker:     'ri-forbid-line',
  dependency:  'ri-links-line',
  information: 'ri-information-line',
};

/** One-line explanation shown under the category picker. */
export const MINUTE_CATEGORY_HINT: Record<MinuteCategory, string> = {
  action:      'Something specific someone has agreed to do, with an owner and a date.',
  decision:    'A choice the meeting settled on, recorded so nobody has to relitigate it later.',
  risk:        'Something that could go wrong in future — not a problem yet, but worth watching.',
  blocker:     'Something stopping work right now. It needs clearing before progress can continue.',
  dependency:  'Something this work needs from another team, supplier, or piece of work.',
  information: 'Context shared for the record. Nobody needs to do anything with it.',
};

/**
 * Categories that carry an owner, a due date and an open/done status.
 * Only actions do — every other category is a statement of fact, not a task.
 */
export const CATEGORY_TAKES_OWNER: ReadonlySet<MinuteCategory> = new Set<MinuteCategory>(['action']);

/** Convenience predicate over {@link CATEGORY_TAKES_OWNER}. */
export const categoryTakesOwner = (category: MinuteCategory): boolean =>
  CATEGORY_TAKES_OWNER.has(category);

// ─── Minutes status ───────────────────────────────────────────────────────────

export const MINUTES_STATUSES: MinutesStatus[] = ['draft', 'circulated', 'approved'];

export const MINUTES_STATUS_LABELS: Record<MinutesStatus, string> = {
  draft:      'Draft',
  circulated: 'Circulated',
  approved:   'Approved',
};

/** Nimbus tone per status. */
export const MINUTES_STATUS_TONE: Record<MinutesStatus, string> = {
  draft:      'neutral',
  circulated: 'warning',
  approved:   'success',
};

/**
 * Which statuses a set of minutes may move to next.
 * `approved` is terminal — approved minutes are read-only.
 */
export const MINUTES_STATUS_TRANSITIONS: Record<MinutesStatus, MinutesStatus[]> = {
  draft:      ['circulated'],
  circulated: ['approved', 'draft'],
  approved:   [],
};

/** Button label for a transition *into* the given status. */
export const MINUTES_ACTION_LABELS: Record<MinutesStatus, string> = {
  circulated: 'Circulate for review',
  approved:   'Approve',
  draft:      'Return to draft',
};
